import { buildApiUrl, httpClient } from "shared/api/httpClient";
import {
  extractApiErrorMessage,
  getAuthHeaders,
  invalidateSessionOnAuthError,
  parseJsonResponse,
} from "shared/api/serviceSupport";
import type {
  NotificationListResponse,
  UnreadCountResponse,
} from "modules/notifications/model/notificationTypes";

const BASE_PATH = "/api/notifications";

interface NotificationRequestOptions {
  method?: "GET" | "POST";
  body?: BodyInit | null;
  signal?: AbortSignal;
}

async function notificationRequest<TResponse>(
  path: string,
  options: NotificationRequestOptions = {},
  fallbackErrorMessage = "Erro ao consultar notificacoes.",
): Promise<TResponse> {
  const method = options.method ?? "GET";
  const response = await httpClient({
    url: buildApiUrl(path),
    method,
    headers: {
      ...getAuthHeaders(),
      ...(options.body ? { "Content-Type": "application/json" } : {}),
    },
    body: options.body,
    signal: options.signal,
    retryAttempts: method === "GET" ? undefined : 0,
  });

  if (!response.ok) {
    if (invalidateSessionOnAuthError(response.status)) {
      throw new Error("Sessao encerrada. Faca login novamente.");
    }
    throw new Error(
      extractApiErrorMessage(response.bodyText) ||
        `${fallbackErrorMessage} (${response.status}).`,
    );
  }

  if (!response.bodyText) {
    return {} as TResponse;
  }

  return parseJsonResponse<TResponse>(response.bodyText, "Resposta vazia.");
}

export function listNotifications(
  page: number = 1,
  pageSize: number = 20,
  unreadOnly: boolean = false,
  signal?: AbortSignal,
): Promise<NotificationListResponse> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (unreadOnly) params.set("unreadOnly", "true");

  return notificationRequest<NotificationListResponse>(
    `${BASE_PATH}?${params.toString()}`,
    { signal },
    "Nao foi possivel listar notificacoes.",
  );
}

export function getUnreadNotificationsCount(
  signal?: AbortSignal,
): Promise<UnreadCountResponse> {
  return notificationRequest<UnreadCountResponse>(
    `${BASE_PATH}/unread-count`,
    { signal },
    "Nao foi possivel consultar a contagem de nao-lidas.",
  );
}

export function markNotificationRead(
  id: string,
  signal?: AbortSignal,
): Promise<{ ok: boolean }> {
  return notificationRequest<{ ok: boolean }>(
    `${BASE_PATH}/${encodeURIComponent(id)}/read`,
    { method: "POST", signal },
    "Nao foi possivel marcar como lida.",
  );
}

export function markAllNotificationsRead(
  signal?: AbortSignal,
): Promise<{ updated: number }> {
  return notificationRequest<{ updated: number }>(
    `${BASE_PATH}/read-all`,
    { method: "POST", signal },
    "Nao foi possivel marcar todas como lidas.",
  );
}
